import React from 'react';
import { Image, Shimmer } from 'react-shimmer';
import styled, { css } from 'styled-components';
import { images } from '../../assets/images';

export type RhImageMode = 'square' | 'rectangle' | 'minirect' | 'full';

type Props = {
  /** Source of the image */
  src: string;
  /** Mode of image. Could be one of 'square' | 'rectangle' | 'minirect' | 'full' */
  mode?: RhImageMode;
  /** Override preset width of image */
  width?: number;
  /** Override preset height of image */
  height?: number;
  /** Override border radius of image */
  radius?: number;
  /** Image style */
  style?: React.ImgHTMLAttributes<HTMLImageElement>['style'];
};

export const RhImage: React.FC<Props> = props => {
  const getStyleOverride = () => {
    let style: any = {};
    if (props.width) style.width = props.width;
    if (props.height) style.height = props.height;
    if (props.radius) style.borderRadius = props.radius;
    return style;
  };
  if (props.mode == null) {
    return <br />;
  }
  return (
    <Image
      src={props.src}
      fallback={
        <BorderRadius style={{ borderRadius: props.radius }}>
          {props.mode === 'full' ? (
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 bg-blue-800 rounded animate-spin" />
              <div>Loading image...</div>
            </div>
          ) : (
            <Shimmer
              width={props.width || dimensions[props.mode || 'rectangle'].width}
              height={props.height || dimensions[props.mode || 'rectangle'].height}
            />
          )}
        </BorderRadius>
      }
      errorFallback={() => <ErrorImage src={images.fallbackImage} style={getStyleOverride()} mode={props.mode} />}
      NativeImgProps={{
        style: {
          ...styles[props.mode || 'rectangle'],
          ...getStyleOverride(),
          ...props.style,
        } as React.CSSProperties,
      }}
    />
  );
};

// image size presets
const dimensions = {
  full: { width: 0, height: 0 },
  square: { width: 75, height: 75 },
  rectangle: { width: 242, height: 150 },
  minirect: { width: 127, height: 86 },
};

const ErrorImage = styled.img<{ mode?: RhImageMode }>`
  object-fit: cover;
  border-radius: 16px;
  ${({ mode = 'rectangle' }) => {
    switch (mode) {
      case 'minirect':
        return css`
          width: ${dimensions.minirect.width}px;
          height: ${dimensions.minirect.height}px;
        `;
      case 'square':
        return css`
          width: ${dimensions.square.width}px;
          height: ${dimensions.square.height}px;
        `;
      case 'full':
        return css`
          width: 100%;
          height: 150px;
        `;
      default:
        return css`
          width: ${dimensions.rectangle.width}px;
          height: ${dimensions.rectangle.height}px;
        `;
    }
  }}
`;

const BorderRadius = styled.div`
  border-radius: 16px;
  overflow: hidden;
`;

const styles = {
  square: {
    objectFit: 'cover',
    width: dimensions.square.width + 'px',
    height: dimensions.square.height + 'px',
    borderRadius: 16,
  },
  rectangle: {
    objectFit: 'cover',
    width: dimensions.rectangle.width + 'px',
    height: dimensions.rectangle.height + 'px',
    borderRadius: 16,
  },
  minirect: {
    objectFit: 'cover',
    width: dimensions.minirect.width + 'px',
    height: dimensions.minirect.height + 'px',
    borderRadius: 16,
  },
  full: {
    objectFit: 'cover',
    width: '100%',
    borderRadius: 16,
  },
};
